// components/DeviceMap.tsx
import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon, LatLngBounds } from 'leaflet';
import { X, RefreshCw, MapPin } from 'lucide-react';
import { Location } from '../types/locations';
import { locationsApi } from '../services/locationsApi';
import { isDeviceOnline } from '../types/devices';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
// You'll need to import these or use a CDN
const createIcon = (color: 'green' | 'red' | 'gray') => {
  const colors = {
    green: '#22c55e',
    red: '#ef4444',
    gray: '#9ca3af'
  };
  
  // SVG marker icon as data URI
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
      <path fill="${colors[color]}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle fill="white" cx="12" cy="9" r="2.5"/>
    </svg>
  `;
  
  return new Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(svg)}`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

const greenIcon = createIcon('green');
const redIcon = createIcon('red');
const grayIcon = createIcon('gray');

// Component to fit map bounds to all markers
interface FitBoundsProps {
  locations: Location[];
}

const FitBounds: React.FC<FitBoundsProps> = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    const validLocations = locations.filter(
      loc => loc.latitude != null && loc.longitude != null
    );

    if (validLocations.length === 0) return;

    if (validLocations.length === 1) {
      map.setView(
        [validLocations[0].latitude!, validLocations[0].longitude!],
        10
      );
    } else {
      const bounds = new LatLngBounds(
        validLocations.map(loc => [loc.latitude!, loc.longitude!])
      );
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);

  return null;
};

interface DeviceMapProps {
  onClose: () => void;
}

const DeviceMap: React.FC<DeviceMapProps> = ({ onClose }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const data = await locationsApi.getAllLocations();
      setLocations(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch locations');
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  // Filter locations that have coordinates
  const mappedLocations = locations.filter(
    loc => loc.latitude != null && loc.longitude != null
  );

  // Count devices
  const totalDevices = locations.reduce(
    (sum, loc) => sum + (loc.devices?.length || 0),
    0
  );
  const onlineDevices = locations.reduce(
    (sum, loc) =>
      sum + (loc.devices?.filter(d => isDeviceOnline(d.onlineStatus)).length || 0),
    0
  );

  // Get marker icon based on device status at location
  const getMarkerIcon = (location: Location) => {
    const devices = location.devices || [];
    if (devices.length === 0) return grayIcon;
    
    const online = devices.filter(d => isDeviceOnline(d.onlineStatus)).length;
    if (online === devices.length) return greenIcon;
    if (online === 0) return redIcon;
    return greenIcon; // Mixed - show as green (has some online)
  };

  // Default center: mid-Atlantic (good for UK + US East Coast view)
  const defaultCenter: [number, number] = [45, -30];
  const defaultZoom = 3;

  return (
    <div className="bg-white rounded-lg shadow-lg max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b">
        <div className="flex items-center">
          <MapPin className="w-5 h-5 text-blue-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Network Map</h2>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={fetchLocations}
            className="flex items-center px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="px-4 py-2 bg-gray-50 border-b flex gap-6 text-sm">
        <div>
          <span className="text-gray-500">Locations:</span>{' '}
          <span className="font-medium">{locations.length}</span>
          {mappedLocations.length < locations.length && (
            <span className="text-gray-400 ml-1">
              ({mappedLocations.length} mapped)
            </span>
          )}
        </div>
        <div>
          <span className="text-gray-500">Devices:</span>{' '}
          <span className="font-medium text-green-600">{onlineDevices} online</span>
          <span className="text-gray-400 mx-1">/</span>
          <span className="font-medium">{totalDevices} total</span>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 min-h-[500px]">
        {error ? (
          <div className="flex items-center justify-center h-full text-red-600">
            {error}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            Loading locations...
          </div>
        ) : mappedLocations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MapPin className="w-12 h-12 mb-2 text-gray-300" />
            <p>No locations with coordinates</p>
            <p className="text-sm mt-1">
              Add latitude and longitude to your locations to see them on the map
            </p>
          </div>
        ) : (
          <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            style={{ height: '500px', width: '100%' }} 
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds locations={mappedLocations} />
            {mappedLocations.map(location => (
              <Marker
                key={location.locationId}
                position={[location.latitude!, location.longitude!]}
                icon={getMarkerIcon(location)}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <h3 className="font-semibold text-gray-900">
                      {location.locationName}
                    </h3>
                    {location.locationType && (
                      <p className="text-xs text-gray-500 mb-2">
                        {location.locationType.typeName}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      {location.address}
                      <br />
                      {location.city}, {location.postCode}
                    </p>
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <p className="text-sm">
                        <span className="text-green-600 font-medium">
                          {location.devices?.filter(d => isDeviceOnline(d.onlineStatus)).length || 0}
                        </span>
                        <span className="text-gray-400"> / </span>
                        <span className="text-gray-600">
                          {location.devices?.length || 0}
                        </span>
                        <span className="text-gray-500"> devices online</span>
                      </p>
                    </div>
                    {location.devices && location.devices.length > 0 && (
                      <div className="mt-2 text-xs">
                        {location.devices.slice(0, 5).map(device => (
                          <div key={device.deviceId} className="flex items-center gap-1">
                            <span
                              className={`w-2 h-2 rounded-full ${
                                isDeviceOnline(device.onlineStatus)
                                  ? 'bg-green-500'
                                  : 'bg-red-500'
                              }`}
                            />
                            <span className="text-gray-600">{device.deviceName}</span>
                          </div>
                        ))}
                        {location.devices.length > 5 && (
                          <p className="text-gray-400 mt-1">
                            +{location.devices.length - 5} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>

      {/* Legend */}
      <div className="px-4 py-2 border-t bg-gray-50 flex gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-500" />
          All devices online
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-red-500" />
          All devices offline
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-gray-400" />
          No devices
        </div>
      </div>
    </div>
  );
};

export default DeviceMap;
